// Browser measurements behind contentFloorPx (lib/fieldLayout), which stays
// pure (z8nrz7e05f). Measured, not hardcoded: a native date picker's width
// differs by browser and locale (Chrome draws mm/dd/yyyy and its calendar icon
// in about 152px, a datetime in about 242px).

import type { ControlMeasure } from './fieldLayout'

/** Used where there is no document to measure in. Chrome's widths. */
const FALLBACK_NATIVE_PX = { date: 152, datetime: 242 }

/** The Input and SelectTrigger text size from sm up (md:text-sm), where fields sit side by side. */
const CONTROL_FONT_PX = 14

const nativeCache = new Map<'date' | 'datetime', number>()
const textCache = new Map<string, number>()
let context: CanvasRenderingContext2D | null | undefined

function controlFont(): string {
  return `${CONTROL_FONT_PX}px ${getComputedStyle(document.body).fontFamily}`
}

function nativePx(type: 'date' | 'datetime'): number {
  const cached = nativeCache.get(type)
  if (cached) return cached
  if (typeof document === 'undefined' || !document.body) return FALLBACK_NATIVE_PX[type]
  // An auto-width input draws at its own width. Same font, padding and border as Input.
  const probe = document.createElement('input')
  probe.type = type === 'date' ? 'date' : 'datetime-local'
  probe.setAttribute('aria-hidden', 'true')
  probe.tabIndex = -1
  Object.assign(probe.style, {
    position: 'absolute',
    visibility: 'hidden',
    width: 'auto',
    boxSizing: 'border-box',
    padding: '0 12px',
    border: '1px solid',
    font: controlFont(),
  })
  document.body.appendChild(probe)
  const width = probe.offsetWidth || FALLBACK_NATIVE_PX[type]
  probe.remove()
  nativeCache.set(type, width)
  return width
}

function textPx(text: string): number {
  const cached = textCache.get(text)
  if (cached !== undefined) return cached
  if (context === undefined) {
    context = typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d')
  }
  // No canvas: a rough average character width, so the floor still grows with the text.
  if (!context) return text.length * CONTROL_FONT_PX * 0.55
  context.font = controlFont()
  const width = context.measureText(text).width
  textCache.set(text, width)
  return width
}

export const browserControlMeasure: ControlMeasure = { nativePx, textPx }
